import React, { Children, cloneElement, useState, useRef } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import { guid, createDataAttributes } from "../../utilities/index";

import ToolTipContent from "./ToolTipContent";
import "./ToolTip.scss";

const ToolTip = ({
  id = guid(),
  trigger,
  content,
  className,
  variant,
  inline,
  position,
  showByDefault,
  type,
  useCloseIcon,
  width,
  nowrap,
  onClose,
  onOpen,
  flexiblePosition,
  dataAttrib,
  ...rest
}) => {
  const mainRef = useRef(null);

  const contentId = `uikit_tooltip_id_${id}`;
  const triggerId = `uikit_tooltip_triggerid_${id}`;

  const [show, setShow] = useState(showByDefault);
  const [isFocused, setIsFocused] = useState(false);
  const [delayHandler, setDelayHandler] = useState(null);

  const wrapperClasses = classnames(className, "uikit__tooltip__wrapper", {
    uikit__tooltip__inline: inline
  });

  const triggerClasses = classnames("uikit__tooltip__trigger");

  /**
   *  handleClickOutside used for closing the tip when user clicks outside the tip area
   *  - applied to event listener on tip "open"
   *  - only closes tips using "hover"
   */
  const handleClickOutside = event => {
    if (
      !(useCloseIcon || showByDefault) &&
      mainRef.current &&
      !mainRef.current.contains(event.target)
    ) {
      closeTooltip();
    }
  };

  /**
   *  handleEscKey used for closing the tip when user hits escape key
   *  - applied to event listener on tip "open"
   *  - only closes tips using "hover"
   */
  const handleEscKey = event => {
    if (!useCloseIcon && isFocused && event.keyCode === 27) {
      closeTooltip();
    }
  };

  const openTooltip = () => {
    setShow(true);
    if (!show && onOpen) onOpen({ id, contentId, triggerId });
    // pay attention to clicks and esc keys to close the tip
    if (
      !showByDefault &&
      !useCloseIcon &&
      document.body.getAttribute(triggerId) !== "true"
    ) {
      document.addEventListener("keydown", handleEscKey, true);
      document.addEventListener("click", handleClickOutside, true);
      document.body.setAttribute(triggerId, "true");
    }
  };

  const closeTooltip = () => {
    setShow(false);
    if (show && onClose) onClose({ id, contentId, triggerId });
    document.removeEventListener("keydown", handleEscKey, true);
    document.removeEventListener("click", handleClickOutside, true);
    document.body.removeAttribute(triggerId);
  };

  /**
   * EVENT HANDLERS
   *
   * Note, we track focus and blur to handle keyboard controls
   * - when user tabs to trigger, then show tip if focused for at least 500ms
   * - when they tab away (lose focus), then hide the tip
   */
  const handleOnFocus = () => {
    setIsFocused(true);
    if (type === "hover" && !show && !(useCloseIcon || showByDefault)) {
      setTimeout(() => {
        openTooltip();
      }, 500);
    }
  };

  const handleOnBlur = () => {
    setIsFocused(false);
    if (type === "hover" && show && !(useCloseIcon || showByDefault))
      setTimeout(() => {
        closeTooltip();
      }, 500);
  };

  const handleClick = () => {
    if (showByDefault || type === "click") {
      if (show) closeTooltip();
      else openTooltip();
    }
  };

  const handleMouseEnter = () => {
    if (!showByDefault && type === "hover") {
      setIsFocused(true);
      clearTimeout(delayHandler);
      openTooltip();
    }
  };

  const handleMouseLeave = () => {
    if (!showByDefault && type === "hover") {
      setDelayHandler(
        setTimeout(() => {
          setIsFocused(false);
          closeTooltip();
        }, 500)
      );
    }
  };

  /**
   * Forcing the button role for web accessibility
   * -- evaluate the JSX for the trigger
   * ... if it's not a button, make sure it has a role of button and a tabIndex
   */
  const _trigger = Children.map(trigger, child => {
    const newProps = { ...child.props };
    if (!child?.props?.tabIndex) newProps.tabIndex = 0;

    if (type === "hover") newProps["aria-describedby"] = contentId;
    else newProps["aria-labelledby"] = contentId;

    if (type !== "hover") newProps.onClick = handleClick;

    newProps.id = triggerId;
    newProps["aria-expanded"] = show;
    newProps.onMouseEnter = handleMouseEnter;
    newProps.onMouseLeave = handleMouseLeave;
    newProps.onFocus = handleOnFocus;
    newProps.onBlur = handleOnBlur;
    newProps.className = triggerClasses;

    return cloneElement(child, newProps);
  });

  /**
   * For HOVER-type tips, we render the tip in the DOM and hide it with CSS...
   * For CLICK-type tips, we don't render in the DOM until the user triggers it
   */
  const renderContentInDom = type === "hover" || (type === "click" && show);

  return (
    <span
      ref={mainRef}
      className={wrapperClasses}
      {...createDataAttributes(dataAttrib)}
      {...rest}
    >
      {_trigger}
      {renderContentInDom && (
        <ToolTipContent
          show={show}
          showByDefault={showByDefault}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          contentId={contentId}
          triggerId={triggerId}
          content={content}
          mainRef={mainRef}
          closeTooltip={closeTooltip}
          variant={variant}
          nowrap={nowrap}
          position={position}
          manuallyClose={useCloseIcon || showByDefault}
          width={width}
          flexiblePosition={flexiblePosition}
        />
      )}
    </span>
  );
};

ToolTip.propTypes = {
  id: propTypes.string,
  trigger: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node
  ]).isRequired,
  content: propTypes.oneOfType([
    propTypes.element,
    propTypes.string,
    propTypes.node
  ]).isRequired,
  className: propTypes.string,
  variant: propTypes.oneOf(["blue", "grey", "black", "white"]),
  position: propTypes.oneOf(["bottom", "top", "left", "right"]),
  inline: propTypes.bool,
  nowrap: propTypes.bool,
  showByDefault: propTypes.bool,
  type: propTypes.oneOf(["hover", "click"]),
  useCloseIcon: propTypes.bool,
  width: propTypes.number,
  onClose: propTypes.func,
  onOpen: propTypes.func,
  flexiblePosition: propTypes.bool,
  dataAttrib: propTypes.arrayOf(Object)
};

ToolTip.defaultProps = {
  variant: "grey",
  position: "top",
  inline: true,
  nowrap: true,
  showByDefault: false,
  type: "click",
  useCloseIcon: false,
  flexiblePosition: true
};

export default ToolTip;
